
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Button, Chip, List, ListItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Alert } from '@mui/material';
import clinicaService from '../../services/clinicaService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const planos = [
    {
        nome: 'Plano Grátis',
        preco: 'R$ 0',
        periodo: '/mês',
        features: [
            { texto: 'Até 50 pacientes', ok: true },
            { texto: '15 emissões de notas', ok: true },
            { texto: 'Até 10 procedimentos', ok: true },
            { texto: '15 lançamentos de despesas/mês', ok: true },
            { texto: '15 lançamentos de faturamento/mês', ok: true },
            { texto: '15 agendamentos/mês', ok: true },
            { texto: 'Confirmação via WhatsApp', ok: false, whatsapp: true },
        ],
        botao: { texto: 'Começar agora', variant: 'outlined' },
        destaque: false,
    },
    {
        nome: 'Plano Pro',
        preco: 'R$ 129,90',
        periodo: '/mês',
        features: [
            { texto: 'Pacientes Ilimitados', ok: true, bold: true },
            { texto: 'Emissão de notas Ilimitada', ok: true, bold: true },
            { texto: 'Procedimentos Ilimitados', ok: true, bold: true },
            { texto: 'Fluxo de caixa Ilimitado', ok: true, bold: true },
            { texto: 'Agendamentos Ilimitados', ok: true, bold: true },
            { texto: 'Confirmação via WhatsApp', ok: true, whatsapp: true, bold: true },
            { texto: 'Receba a nota fiscal automaticamente no WhatsApp', ok: true, whatsapp: true, bold: true },
            { texto: 'Anexar comprovantes das despesas', ok: true, bold: true },
            { texto: 'Suporte Prioritário', ok: true },
        ],
        botao: { texto: 'Assinar Plano Pro', variant: 'contained' },
        destaque: true,
    },
];

const Assinatura = () => {
    const { user, setUser } = useAuth();
    const planoAtual = user?.plano || user?.clinica?.plano || 'FREE';
    const navigate = useNavigate();

    return (
        <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto', py: 4, px: { xs: 1, sm: 2 } }}>
            <Box className="header" sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h3" fontWeight={800} letterSpacing={-1} mb={1}>
                    Escolha seu plano
                </Typography>
                <Typography color="text.secondary" fontSize={18}>
                    Simples, transparente e focado no crescimento da sua clínica.
                </Typography>
            </Box>
            <Grid container spacing={4} justifyContent="center" className="pricing-grid">
                {planos.map((plano, idx) => (
                    <Grid item xs={12} sm={6} md={6} key={plano.nome}>
                        <Card
                            elevation={plano.destaque ? 8 : 2}
                            sx={{
                                borderRadius: 3,
                                border:
                                    plano.nome === 'Plano Grátis' && planoAtual === 'FREE'
                                        ? '2.5px solid #00B389'
                                        : plano.destaque
                                        ? '2px solid #FF8C00'
                                        : '1px solid #EDF2F7',
                                boxShadow: plano.destaque ? '0 10px 30px rgba(255, 140, 0, 0.1)' : undefined,
                                position: 'relative',
                                p: 0,
                                pt: plano.destaque ? 4 : 0,
                                pb: 2,
                                minHeight: 480,
                                display: 'flex',
                                flexDirection: 'column',
                                textAlign: 'left',
                            }}
                            className={plano.destaque ? 'featured' : ''}
                        >
                            {/* Badge de recomendado e badge de plano atual */}
                            {plano.destaque && (
                                <Chip
                                    label="Recomendado"
                                    color="warning"
                                    sx={{ position: 'absolute', top: 16, right: 32, fontWeight: 700, fontSize: 13, px: 2 }}
                                />
                            )}
                            {plano.nome === 'Plano Grátis' && planoAtual === 'FREE' && ( 
                                <Chip 
                                    label="Plano atual" 
                                    color="success" 
                                    sx={{ position: 'absolute', top: 16, right: 32, fontWeight: 700, fontSize: 13, px: 2, zIndex: 2, color: '#fff' }} 
                                /> 
                            )}
                            {plano.nome === 'Plano Pro' && planoAtual === 'PRO' && (
                                <Chip
                                    label="Plano atual"
                                    color="warning"
                                    sx={{ position: 'absolute', top: 16, right: 32, fontWeight: 700, fontSize: 13, px: 2, zIndex: 2, color: '#fff' }}
                                />
                            )}
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                                <Typography className="plan-name" variant="h6" fontWeight={700} mb={1} sx={plano.destaque ? { color: '#FF8C00' } : {}}>
                                    {plano.nome}
                                </Typography>
                                <Box className="price" sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 3 }}>
                                    <Typography variant="h3" fontWeight={800}>
                                        {plano.preco}
                                    </Typography>
                                    <Typography component="span" fontSize={18} color="text.secondary">
                                        {plano.periodo}
                                    </Typography>
                                </Box>
                                <List sx={{ mb: 4, flexGrow: 1 }}> 
                                    {plano.features.map((f, i) => (
                                        <ListItem key={i} disableGutters sx={{ mb: 1, color: f.ok ? '#4A5568' : '#A0AEC0', textDecoration: f.ok ? 'none' : 'line-through' }}>
                                            <ListItemIcon sx={{ minWidth: 32, color: f.ok ? (f.whatsapp ? '#25D366' : '#FF8C00') : '#CBD5E0' }}>
                                                {f.ok ? (
                                                    f.whatsapp ? <WhatsAppIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />
                                                ) : (
                                                    <CancelIcon fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <span style={{ fontWeight: f.bold ? 700 : 400 }}>
                                                        {f.texto}
                                                    </span>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                {/* Botão: se já for PRO sem assinatura recorrente, mostra "Configurar Pagamento"; se tem assinatura, "Ver Plano" */}
                                {plano.nome === 'Plano Pro' && planoAtual === 'PRO' && !user?.clinica?.asaasSubscriptionId ? (
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="large"
                                        fullWidth
                                        sx={{ fontWeight: 700, borderRadius: 2, py: 1.5, fontSize: 17, boxShadow: '0 4px 14px rgba(255, 140, 0, 0.3)' }}
                                        onClick={() => navigate('/usuario/pagamentopro')}
                                    >
                                        Configurar Pagamento Recorrente
                                    </Button>
                                ) : plano.nome === 'Plano Pro' && planoAtual === 'PRO' ? (
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        size="large"
                                        fullWidth
                                        sx={{ fontWeight: 700, borderRadius: 2, py: 1.5, fontSize: 17, boxShadow: plano.destaque ? '0 4px 14px rgba(255, 140, 0, 0.3)' : undefined }}
                                        onClick={() => navigate('/usuario/plano-pro')}
                                    >
                                        Ver Plano
                                    </Button>
                                ) : null}
                                {/* Botão "Assinar Plano Pro" só aparece se não for PRO */}
                                {plano.nome === 'Plano Pro' && planoAtual !== 'PRO' && (
                                    <Button
                                        variant={plano.botao.variant}
                                        color={plano.destaque ? 'warning' : 'inherit'}
                                        size="large"
                                        fullWidth
                                        sx={{ fontWeight: 700, borderRadius: 2, py: 1.5, fontSize: 17, boxShadow: plano.destaque ? '0 4px 14px rgba(255, 140, 0, 0.3)' : undefined }}
                                        onClick={() => navigate('/usuario/pagamentopro')}
                                    >
                                        {plano.botao.texto}
                                    </Button>
                                )}
                                {/* Não exibe botão para o plano grátis */}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

        </Box>
    );
};

export default Assinatura;
